import React, { useEffect, useState } from 'react';
import { employeesAPI, timeRecordsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { 
  Clock, MapPin, LogIn, LogOut, Coffee, 
  Users, FileText, AlertCircle, Upload, Download,
  CheckCircle, XCircle, Loader2, Edit2, Trash2, Save,
  MessageSquare, FileCheck, Eye, ThumbsUp, ThumbsDown
} from 'lucide-react';
import { getUser, isAdmin } from '@/lib/auth';
import api from '@/lib/api';

// Configuração de geofencing - distância máxima da escola em metros
const DISTANCIA_MAXIMA_METROS = 100; // PONTO_MAX_DISTANCE_METERS = 100

// Timezone oficial: America/Sao_Paulo
const SP_TZ = 'America/Sao_Paulo';
const spNow = () => new Date(new Date().toLocaleString('en-US', { timeZone: SP_TZ }));
const spDate = (d) => { const dt = new Date(d); return new Date(dt.toLocaleString('en-US', { timeZone: SP_TZ })); };
const spFormatTime = (ts) => new Date(ts).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: SP_TZ });
const spFormatDate = (ts, opts) => new Date(ts).toLocaleDateString('pt-BR', { ...opts, timeZone: SP_TZ });
const spFormatDateTime = (ts) => new Date(ts).toLocaleString('pt-BR', { timeZone: SP_TZ });
const spToday = () => { const n = spNow(); return n.getFullYear() + '-' + String(n.getMonth()+1).padStart(2,'0') + '-' + String(n.getDate()).padStart(2,'0'); };
const spMonth = () => { const n = spNow(); return n.getFullYear() + '-' + String(n.getMonth()+1).padStart(2,'0'); };

export default function TimeRecords() {
  const [employees, setEmployees] = useState([]);
  const [records, setRecords] = useState([]);
  const [timesheets, setTimesheets] = useState([]);
  const [payrollReport, setPayrollReport] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(spMonth());
  
  // Clock in dialog
  const [clockDialogOpen, setClockDialogOpen] = useState(false);
  const [clockEmployee, setClockEmployee] = useState('');
  const [clockType, setClockType] = useState('entrada');
  const [location, setLocation] = useState(null);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [distanciaEscola, setDistanciaEscola] = useState(null);
  
  // Justificativa dialog
  const [justificativaDialogOpen, setJustificativaDialogOpen] = useState(false);
  const [justificativaTipo, setJustificativaTipo] = useState('atraso');
  const [justificativaTexto, setJustificativaTexto] = useState('');
  const [justificativaDate, setJustificativaDate] = useState(spToday());
  const [atestadoFile, setAtestadoFile] = useState(null);
  const [uploadingAtestado, setUploadingAtestado] = useState(false);
  
  // Justificativas list (para admin/coordenação)
  const [justificativas, setJustificativas] = useState([]);
  const [showJustificativasModal, setShowJustificativasModal] = useState(false);
  
  // Edit record dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [editTimestamp, setEditTimestamp] = useState('');
  const [editType, setEditType] = useState('');
  const [editNotes, setEditNotes] = useState('');

  // Edit timesheet dialog (folha mensal)
  const [editTimesheetDialogOpen, setEditTimesheetDialogOpen] = useState(false);
  const [editingTimesheet, setEditingTimesheet] = useState(null);
  const [editEntry, setEditEntry] = useState('');
  const [editLunchOut, setEditLunchOut] = useState('');
  const [editLunchIn, setEditLunchIn] = useState('');
  const [editExit, setEditExit] = useState('');

  useEffect(() => {
    loadData();
    loadJustificativas();
  }, []);

  useEffect(() => {
    if (selectedMonth) {
      loadTimesheets();
    }
  }, [selectedMonth, selectedEmployee]);

  const loadData = async () => {
    try {
      const [employeesRes, recordsRes] = await Promise.all([
        employeesAPI.list({ active: true }),
        timeRecordsAPI.list({})
      ]);
      setEmployees(employeesRes.data);
      setRecords(recordsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const loadTimesheets = async () => {
    try {
      const params = { month: selectedMonth };
      if (selectedEmployee) {
        params.employee_id = selectedEmployee;
      }
      const response = await timeRecordsAPI.getDailyTimesheets(params);
      setTimesheets(response.data);
    } catch (error) {
      toast.error('Erro ao carregar folha de ponto');
    }
  };

  const loadPayrollReport = async () => {
    try {
      const response = await timeRecordsAPI.getPayrollReport(selectedMonth);
      setPayrollReport(response.data);
    } catch (error) {
      toast.error('Erro ao gerar relatório');
    }
  };

  const loadJustificativas = async () => {
    try {
      const response = await api.get('/time-records/justificativas', {
        params: { month: selectedMonth }
      });
      setJustificativas(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar justificativas:', error);
      setJustificativas([]);
    }
  };

  // Função para calcular distância entre dois pontos (Haversine)
  const calcularDistancia = (lat1, lon1, lat2, lon2) => {
    const R = 6371000; // Raio da Terra em metros
    const phi1 = (lat1 * Math.PI) / 180;
    const phi2 = (lat2 * Math.PI) / 180;
    const deltaPhi = ((lat2 - lat1) * Math.PI) / 180;
    const deltaLambda = ((lon2 - lon1) * Math.PI) / 180;

    const a = Math.sin(deltaPhi / 2) ** 2 + Math.cos(phi1) * Math.cos(phi2) * Math.sin(deltaLambda / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  };

  // Coordenadas da escola (configuradas)
  const ESCOLA_LAT = -23.618153;
  const ESCOLA_LNG = -46.790322;

  const getLocation = () => {
    if (!navigator.geolocation) {
      setLocationError('Geolocalização não suportada pelo navegador');
      return;
    }
    
    setGettingLocation(true);
    setLocationError('');
    setDistanciaEscola(null);
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        
        // Calcular distância da escola
        const distancia = calcularDistancia(lat, lng, ESCOLA_LAT, ESCOLA_LNG);
        setDistanciaEscola(Math.round(distancia));
        
        setLocation({
          latitude: lat,
          longitude: lng,
          accuracy: position.coords.accuracy
        });
        setGettingLocation(false);
        
        if (distancia <= DISTANCIA_MAXIMA_METROS) {
          toast.success(`Localização capturada! Você está a ${Math.round(distancia)}m da escola.`);
        } else {
          toast.warning(`Você está a ${Math.round(distancia)}m da escola. O limite é ${DISTANCIA_MAXIMA_METROS}m.`);
        }
      },
      (error) => {
        setGettingLocation(false);
        let errorMsg = 'Erro ao obter localização';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMsg = 'Permissão de localização negada. Habilite nas configurações do navegador.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMsg = 'Localização indisponível no momento.';
            break;
          case error.TIMEOUT:
            errorMsg = 'Tempo esgotado ao obter localização.';
            break;
          default:
            errorMsg = 'Erro desconhecido ao obter localização.';
        }
        setLocationError(errorMsg);
        toast.error(errorMsg);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const handleClockIn = async () => {
    if (!clockEmployee) {
      toast.error('Selecione um funcionário');
      return;
    }
    
    if (!location) {
      toast.error('Capture a localização para registrar o ponto');
      return;
    }
    
    // Verificar distância da escola
    if (distanciaEscola !== null && distanciaEscola > DISTANCIA_MAXIMA_METROS) {
      toast.error(`Você está a ${distanciaEscola}m da escola. O ponto só pode ser registrado a até ${DISTANCIA_MAXIMA_METROS}m.`);
      return;
    }
    
    try {
      await timeRecordsAPI.create({
        employee_id: clockEmployee,
        record_type: clockType,
        latitude: location.latitude,
        longitude: location.longitude
      });
      
      const employee = employees.find(e => e.id === clockEmployee);
      toast.success(`Ponto registrado: ${employee?.name} - ${getClockTypeLabel(clockType)}`);
      
      setClockDialogOpen(false);
      setClockEmployee('');
      setClockType('entrada');
      setLocation(null);
      setDistanciaEscola(null);
      loadData();
      loadTimesheets();
    } catch (error) {
      // Verificar se é erro de distância do backend
      const errorMsg = error.response?.data?.detail || 'Erro ao registrar ponto';
      toast.error(errorMsg);
    }
  };

  // Funções para justificativas
  const handleSubmitJustificativa = async () => {
    if (!justificativaTexto.trim()) {
      toast.error('Preencha a justificativa');
      return;
    }
    
    const currentUser = getUser();
    const myEmployee = employees.find(e => 
      e.email === currentUser?.email || e.user_id === currentUser?.id
    );
    
    if (!myEmployee) {
      toast.error('Funcionário não encontrado');
      return;
    }
    
    try {
      let atestadoUrl = null;
      
      // Upload do atestado se houver
      if (atestadoFile) {
        setUploadingAtestado(true);
        const formData = new FormData();
        formData.append('file', atestadoFile);
        formData.append('folder', 'atestados');
        
        try {
          const uploadRes = await api.post('/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          atestadoUrl = uploadRes.data.url;
        } catch (uploadError) {
          console.error('Erro ao fazer upload:', uploadError);
          // Continuar sem o atestado
        }
        setUploadingAtestado(false);
      }
      
      await api.post('/time-records/justificativa', {
        employee_id: myEmployee.id,
        date: justificativaDate,
        tipo: justificativaTipo,
        justificativa: justificativaTexto,
        atestado_url: atestadoUrl
      });
      
      toast.success('Justificativa enviada com sucesso!');
      setJustificativaDialogOpen(false);
      setJustificativaTexto('');
      setJustificativaTipo('atraso');
      setAtestadoFile(null);
      loadJustificativas();
    } catch (error) {
      toast.error('Erro ao enviar justificativa');
    }
  };

  const handleAtualizarStatusJustificativa = async (justificativaId, status) => {
    try {
      await api.put(`/time-records/justificativa/${justificativaId}?status=${status}`);
      toast.success(`Justificativa ${status === 'aprovado' ? 'aprovada' : 'rejeitada'}`);
      loadJustificativas();
    } catch (error) {
      toast.error('Erro ao atualizar justificativa');
    }
  };

  const handleMarkAbsence = async (timesheetId, absence, justified) => {
    try {
      await timeRecordsAPI.updateTimesheet(timesheetId, { absence, absence_justified: justified });
      toast.success('Atualizado com sucesso');
      loadTimesheets();
    } catch (error) {
      toast.error('Erro ao atualizar');
    }
  };

  // Edit time record
  const handleOpenEditDialog = (record) => {
    setEditingRecord(record);
    // Format timestamp for datetime-local input using SP timezone
    const sp = spDate(record.timestamp);
    const formatted = sp.getFullYear() + '-' + String(sp.getMonth()+1).padStart(2,'0') + '-' + String(sp.getDate()).padStart(2,'0') + 'T' + String(sp.getHours()).padStart(2,'0') + ':' + String(sp.getMinutes()).padStart(2,'0');
    setEditTimestamp(formatted);
    setEditType(record.record_type);
    setEditNotes(record.notes || '');
    setEditDialogOpen(true);
  };

  const handleUpdateRecord = async () => {
    if (!editingRecord) return;
    
    try {
      await api.put(`/time-records/${editingRecord.id}`, {
        timestamp: editTimestamp + ':00',
        record_type: editType,
        notes: editNotes
      });
      toast.success('Registro atualizado com sucesso');
      setEditDialogOpen(false);
      setEditingRecord(null);
      loadData();
      loadTimesheets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar registro');
    }
  };

  const handleDeleteRecord = async (recordId) => {
    if (!window.confirm('Tem certeza que deseja excluir este registro de ponto?')) {
      return;
    }
    
    try {
      await api.delete(`/time-records/${recordId}`);
      toast.success('Registro excluído com sucesso');
      loadData();
      loadTimesheets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir registro');
    }
  };

  // Edit timesheet (folha mensal)
  const handleOpenEditTimesheetDialog = (sheet) => {
    setEditingTimesheet(sheet);
    setEditEntry(sheet.entry_time || '');
    setEditLunchOut(sheet.lunch_out_time || '');
    setEditLunchIn(sheet.lunch_in_time || '');
    setEditExit(sheet.exit_time || '');
    setEditTimesheetDialogOpen(true);
  };

  const handleUpdateTimesheet = async () => {
    if (!editingTimesheet) return;
    
    try {
      await timeRecordsAPI.updateTimesheet(editingTimesheet.id, {
        entry_time: editEntry || null,
        lunch_out_time: editLunchOut || null,
        lunch_in_time: editLunchIn || null,
        exit_time: editExit || null
      });
      toast.success('Horários atualizados com sucesso');
      setEditTimesheetDialogOpen(false);
      setEditingTimesheet(null);
      loadTimesheets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar horários');
    }
  };

  const handleDeleteTimesheet = async (timesheetId) => {
    if (!window.confirm('Tem certeza que deseja excluir este registro da folha de ponto?')) {
      return;
    }
    
    try {
      await timeRecordsAPI.deleteTimesheet(timesheetId);
      toast.success('Registro da folha excluído com sucesso');
      loadTimesheets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir registro');
    }
  };

  const getClockTypeLabel = (type) => {
    const labels = {
      entrada: 'Entrada',
      saida_almoco: 'Saída Almoço',
      retorno_almoco: 'Retorno Almoço',
      saida: 'Saída'
    };
    return labels[type] || type;
  };

  const getClockTypeIcon = (type) => {
    const icons = {
      entrada: <LogIn className="w-4 h-4 text-green-500" />,
      saida_almoco: <Coffee className="w-4 h-4 text-amber-500" />,
      retorno_almoco: <Coffee className="w-4 h-4 text-blue-500" />,
      saida: <LogOut className="w-4 h-4 text-red-500" />
    };
    return icons[type] || <Clock className="w-4 h-4" />;
  };

  // Group timesheets by employee
  const groupedTimesheets = {};
  timesheets.forEach(ts => {
    if (!groupedTimesheets[ts.employee_id]) {
      groupedTimesheets[ts.employee_id] = [];
    }
    groupedTimesheets[ts.employee_id].push(ts);
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Verificar se é admin para mostrar todas as funcionalidades
  const userIsAdmin = isAdmin();
  const currentUser = getUser();

  // Para não-admins, mostrar apenas interface de registro de ponto
  if (!userIsAdmin) {
    // Buscar o funcionário vinculado ao usuário logado
    const myEmployee = employees.find(e => 
      e.email === currentUser?.email || 
      e.user_id === currentUser?.id
    );
    
    const myTodayRecords = myEmployee ? records.filter(r => {
      const recordDate = spDate(r.timestamp);
      const todaySp = spNow();
      return r.employee_id === myEmployee.id && recordDate.toDateString() === todaySp.toDateString();
    }) : [];

    return (
      <div className="p-6 space-y-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold" data-testid="time-records-title">Registrar Ponto</h1>
          <p className="text-muted-foreground mt-1">Registre sua entrada e saída</p>
        </div>

        {/* Card com registros de hoje do usuário */}
        <Card className="max-w-lg mx-auto">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 justify-center">
              <Clock className="w-5 h-5" />
              Meus Registros de Hoje
            </CardTitle>
          </CardHeader>
          <CardContent>
            {myTodayRecords.length > 0 ? (
              <div className="space-y-3">
                {myTodayRecords.map(record => (
                  <div key={record.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-2">
                      {getClockTypeIcon(record.record_type)}
                      <span className="font-medium">{getClockTypeLabel(record.record_type)}</span>
                    </div>
                    <span className="text-muted-foreground">
                      {spFormatTime(record.timestamp)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-4">
                Nenhum registro hoje
              </p>
            )}
          </CardContent>
        </Card>

        {/* Botão grande de registrar ponto */}
        <div className="flex flex-col sm:flex-row justify-center gap-3">
          <Button 
            size="lg" 
            className="h-16 px-8 text-lg"
            onClick={() => {
              if (myEmployee) {
                setClockEmployee(myEmployee.id);
              }
              setClockDialogOpen(true);
            }}
          >
            <MapPin className="w-6 h-6 mr-3" />
            Registrar Ponto
          </Button>
          
          <Button 
            size="lg" 
            variant="outline"
            className="h-16 px-6 text-lg border-orange-300 text-orange-700 hover:bg-orange-50"
            onClick={() => setJustificativaDialogOpen(true)}
          >
            <MessageSquare className="w-6 h-6 mr-3" />
            Justificar Atraso/Falta
          </Button>
        </div>

        {/* Dialog de registro de ponto simplificado */}
        <Dialog open={clockDialogOpen} onOpenChange={(open) => { 
          setClockDialogOpen(open); 
          if (!open) {
            setLocation(null);
            setLocationError('');
            setDistanciaEscola(null);
          }
        }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Registrar Ponto</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Tipo de Registro</Label>
                <Select value={clockType} onValueChange={setClockType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="entrada">
                      <div className="flex items-center gap-2">
                        <LogIn className="w-4 h-4 text-green-500" />
                        Entrada
                      </div>
                    </SelectItem>
                    <SelectItem value="saida_almoco">
                      <div className="flex items-center gap-2">
                        <Coffee className="w-4 h-4 text-amber-500" />
                        Saída Almoço
                      </div>
                    </SelectItem>
                    <SelectItem value="retorno_almoco">
                      <div className="flex items-center gap-2">
                        <Coffee className="w-4 h-4 text-blue-500" />
                        Retorno Almoço
                      </div>
                    </SelectItem>
                    <SelectItem value="saida">
                      <div className="flex items-center gap-2">
                        <LogOut className="w-4 h-4 text-red-500" />
                        Saída
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Localização GPS</Label>
                <div className="border rounded-lg p-4 bg-muted/30">
                  {location ? (
                    <div className="space-y-3">
                      {distanciaEscola !== null && distanciaEscola <= DISTANCIA_MAXIMA_METROS ? (
                        <div className="flex items-center gap-2 text-green-600">
                          <CheckCircle className="w-5 h-5" />
                          <span className="font-medium">Localização válida!</span>
                        </div>
                      ) : distanciaEscola !== null ? (
                        <div className="flex items-center gap-2 text-red-600">
                          <XCircle className="w-5 h-5" />
                          <span className="font-medium">Fora da área permitida</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-green-600">
                          <CheckCircle className="w-5 h-5" />
                          <span className="font-medium">Localização capturada!</span>
                        </div>
                      )}
                      
                      {distanciaEscola !== null && (
                        <div className={`p-2 rounded-lg text-sm ${
                          distanciaEscola <= DISTANCIA_MAXIMA_METROS 
                            ? 'bg-green-50 text-green-700' 
                            : 'bg-red-50 text-red-700'
                        }`}>
                          <MapPin className="w-4 h-4 inline mr-1" />
                          Distância da escola: <strong>{distanciaEscola}m</strong>
                          {distanciaEscola > DISTANCIA_MAXIMA_METROS && (
                            <span> (máximo permitido: {DISTANCIA_MAXIMA_METROS}m)</span>
                          )}
                        </div>
                      )}
                      
                      {location.accuracy && (
                        <p className="text-xs text-muted-foreground">
                          Precisão GPS: ~{Math.round(location.accuracy)} metros
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      {gettingLocation ? (
                        <div className="flex flex-col items-center gap-2">
                          <Loader2 className="w-8 h-8 animate-spin text-primary" />
                          <p className="text-sm text-muted-foreground">Obtendo localização...</p>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-2">
                          <MapPin className="w-12 h-12 text-muted-foreground/50" />
                          <p className="text-sm text-muted-foreground">Clique para capturar sua localização</p>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {locationError && (
                    <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-red-600 text-sm flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      {locationError}
                    </div>
                  )}
                </div>
                
                <div className="flex gap-2">
                  {!location && (
                    <Button 
                      type="button" 
                      onClick={getLocation} 
                      className="flex-1"
                      disabled={gettingLocation}
                    >
                      {gettingLocation ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Capturando...
                        </>
                      ) : (
                        <>
                          <MapPin className="w-4 h-4 mr-2" />
                          Capturar Localização
                        </>
                      )}
                    </Button>
                  )}
                  {location && (
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => { setLocation(null); getLocation(); }}
                      className="flex-1"
                    >
                      <MapPin className="w-4 h-4 mr-2" />
                      Atualizar Localização
                    </Button>
                  )}
                </div>
              </div>

              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center gap-2 text-blue-700 text-sm">
                  <Clock className="w-4 h-4" />
                  <span>
                    Horario atual: <strong>{spNow().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</strong>
                  </span>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setClockDialogOpen(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleClockIn} 
                disabled={!clockEmployee || !location || (distanciaEscola !== null && distanciaEscola > DISTANCIA_MAXIMA_METROS)}
              >
                Registrar Ponto
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Interface completa para admin
  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold" data-testid="time-records-title">Controle de Ponto</h1>
          <p className="text-muted-foreground">Registros de entrada, saída e almoço</p>
        </div>
        <div className="flex gap-2">
          {justificativas.filter(j => j.status === 'pendente').length > 0 && (
            <Button 
              variant="outline" 
              className="border-orange-300 text-orange-700 hover:bg-orange-50"
              onClick={() => setShowJustificativasModal(true)}
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Justificativas ({justificativas.filter(j => j.status === 'pendente').length})
            </Button>
          )}
          <Button onClick={() => setClockDialogOpen(true)} data-testid="clock-in-btn">
            <MapPin className="w-4 h-4 mr-2" />
            Registrar Ponto
          </Button>
        </div>
      </div>

      <Tabs defaultValue="daily" className="w-full">
        <TabsList>
          <TabsTrigger value="daily">Registros do Dia</TabsTrigger>
          <TabsTrigger value="monthly">Folha Mensal</TabsTrigger>
          <TabsTrigger value="payroll">Relatório Folha</TabsTrigger>
        </TabsList>

        {/* Daily Records */}
        <TabsContent value="daily" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Registros de Hoje - {spNow().toLocaleDateString('pt-BR')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {employees.map(employee => {
                  const todayRecords = records.filter(r => {
                    const recordDate = spDate(r.timestamp);
                    const todaySp = spNow();
                    return r.employee_id === employee.id && recordDate.toDateString() === todaySp.toDateString();
                  });

                  return (
                    <div key={employee.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center text-white font-medium">
                          {employee.name?.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium">{employee.name}</p>
                          <p className="text-sm text-muted-foreground">{employee.role}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-6">
                        {todayRecords.length > 0 ? (
                          todayRecords.map(record => (
                            <div key={record.id} className="flex items-center gap-2 text-sm">
                              {getClockTypeIcon(record.record_type)}
                              <span>{getClockTypeLabel(record.record_type)}</span>
                              <span className="text-muted-foreground">
                                {spFormatTime(record.timestamp)}
                              </span>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-6 w-6 p-0 text-blue-500 hover:text-blue-700"
                                onClick={() => handleOpenEditDialog(record)}
                                data-testid={`edit-record-${record.id}`}
                              >
                                <Edit2 className="w-3 h-3" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                                onClick={() => handleDeleteRecord(record.id)}
                                data-testid={`delete-record-${record.id}`}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          ))
                        ) : (
                          <span className="text-sm text-muted-foreground">Sem registros hoje</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Monthly Timesheet */}
        <TabsContent value="monthly" className="space-y-4">
          {/* Card de Justificativas Pendentes - apenas para admin/coordenação */}
          {justificativas.filter(j => j.status === 'pendente').length > 0 && (
            <Card className="border-orange-200 bg-orange-50/50">
              <CardHeader className="pb-2">
                <div className="flex justify-between items-center">
                  <CardTitle className="text-orange-700 flex items-center gap-2 text-base">
                    <MessageSquare className="w-5 h-5" />
                    Justificativas Pendentes
                  </CardTitle>
                  <Button 
                    size="sm" 
                    variant="outline"
                    className="border-orange-300 text-orange-700 hover:bg-orange-100"
                    onClick={() => setShowJustificativasModal(true)}
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Ver Todas ({justificativas.filter(j => j.status === 'pendente').length})
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="flex flex-wrap gap-2">
                  {justificativas.filter(j => j.status === 'pendente').slice(0, 3).map(j => (
                    <Badge key={j.id} variant="outline" className="bg-white">
                      {j.employee_name} - {spFormatDate(j.date + 'T12:00:00')}
                    </Badge>
                  ))}
                  {justificativas.filter(j => j.status === 'pendente').length > 3 && (
                    <Badge variant="outline" className="bg-white">
                      +{justificativas.filter(j => j.status === 'pendente').length - 3} mais
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
          
          <Card>
            <CardContent className="p-4">
              <div className="flex gap-4">
                <div className="space-y-2">
                  <Label>Mês</Label>
                  <Input
                    type="month"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Funcionário</Label>
                  <Select value={selectedEmployee || "all"} onValueChange={(v) => setSelectedEmployee(v === "all" ? "" : v)}>
                    <SelectTrigger className="w-[250px]">
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {employees.map(e => (
                        <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {Object.entries(groupedTimesheets).map(([employeeId, sheets]) => {
            const employee = employees.find(e => e.id === employeeId);
            const totalHours = sheets.reduce((sum, s) => sum + (s.total_hours || 0), 0);
            const extraHours = sheets.reduce((sum, s) => sum + (s.extra_hours || 0), 0);
            const absences = sheets.filter(s => s.absence).length;

            return (
              <Card key={employeeId}>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle className="flex items-center gap-2">
                      <Users className="w-5 h-5" />
                      {employee?.name}
                    </CardTitle>
                    <div className="flex gap-4 text-sm">
                      <Badge variant="outline">
                        Total: {totalHours.toFixed(1)}h
                      </Badge>
                      <Badge variant="secondary">
                        Extras: {extraHours.toFixed(1)}h
                      </Badge>
                      {absences > 0 && (
                        <Badge variant="destructive">
                          Faltas: {absences}
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="text-left p-2">Data</th>
                          <th className="text-center p-2">Entrada</th>
                          <th className="text-center p-2">Saída Almoço</th>
                          <th className="text-center p-2">Retorno Almoço</th>
                          <th className="text-center p-2">Saída</th>
                          <th className="text-center p-2">Total</th>
                          <th className="text-center p-2">Extras</th>
                          <th className="text-center p-2">Status</th>
                          <th className="text-center p-2">Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sheets.sort((a, b) => a.date.localeCompare(b.date)).map(sheet => (
                          <tr key={sheet.id} className="border-t">
                            <td className="p-2">
                              {spFormatDate(sheet.date + 'T12:00:00', { weekday: 'short', day: '2-digit', month: '2-digit' })}
                            </td>
                            <td className="p-2 text-center">{sheet.entry_time || '-'}</td>
                            <td className="p-2 text-center">{sheet.lunch_out_time || '-'}</td>
                            <td className="p-2 text-center">{sheet.lunch_in_time || '-'}</td>
                            <td className="p-2 text-center">{sheet.exit_time || '-'}</td>
                            <td className="p-2 text-center font-medium">{sheet.total_hours?.toFixed(1) || 0}h</td>
                            <td className="p-2 text-center">
                              {sheet.extra_hours > 0 && (
                                <Badge variant="secondary">{sheet.extra_hours.toFixed(1)}h</Badge>
                              )}
                            </td>
                            <td className="p-2 text-center">
                              {sheet.absence ? (
                                sheet.absence_justified ? (
                                  <Badge variant="outline" className="bg-amber-50">
                                    <AlertCircle className="w-3 h-3 mr-1" />
                                    Just.
                                  </Badge>
                                ) : (
                                  <Badge variant="destructive">
                                    <XCircle className="w-3 h-3 mr-1" />
                                    Falta
                                  </Badge>
                                )
                              ) : sheet.entry_time && sheet.exit_time ? (
                                <Badge variant="default" className="bg-green-500">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  OK
                                </Badge>
                              ) : (
                                <Badge variant="outline">Incompleto</Badge>
                              )}
                            </td>
                            <td className="p-2 text-center">
                              <div className="flex justify-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleOpenEditTimesheetDialog(sheet)}
                                  title="Editar Horários"
                                  data-testid={`edit-timesheet-${sheet.id}`}
                                >
                                  <Edit2 className="w-4 h-4 text-blue-500" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleMarkAbsence(sheet.id, true, false)}
                                  title="Marcar Falta"
                                >
                                  <XCircle className="w-4 h-4 text-red-500" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleMarkAbsence(sheet.id, true, true)}
                                  title="Falta Justificada"
                                >
                                  <AlertCircle className="w-4 h-4 text-amber-500" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleMarkAbsence(sheet.id, false, false)}
                                  title="Remover Falta"
                                >
                                  <CheckCircle className="w-4 h-4 text-green-500" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteTimesheet(sheet.id)}
                                  title="Excluir Registro"
                                  data-testid={`delete-timesheet-${sheet.id}`}
                                >
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </TabsContent>

        {/* Payroll Report */}
        <TabsContent value="payroll" className="space-y-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex gap-4 items-end">
                <div className="space-y-2">
                  <Label>Mês</Label>
                  <Input
                    type="month"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                  />
                </div>
                <Button onClick={loadPayrollReport}>
                  <FileText className="w-4 h-4 mr-2" />
                  Gerar Relatório
                </Button>
              </div>
            </CardContent>
          </Card>

          {payrollReport && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Relatório para Folha de Pagamento - {payrollReport.month}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-3">Funcionário</th>
                        <th className="text-left p-3">CPF</th>
                        <th className="text-left p-3">PIS/PASEP</th>
                        <th className="text-center p-3">Horas</th>
                        <th className="text-center p-3">Extras</th>
                        <th className="text-center p-3">Faltas</th>
                        <th className="text-right p-3">Base</th>
                        <th className="text-right p-3">Extras</th>
                        <th className="text-right p-3">Total</th>
                        <th className="text-left p-3">Banco/PIX</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payrollReport.employees.map(emp => (
                        <tr key={emp.employee_id} className="border-t">
                          <td className="p-3">
                            <div>
                              <p className="font-medium">{emp.name}</p>
                              <p className="text-xs text-muted-foreground">{emp.payment_type === 'horista' ? 'Horista' : 'Mensalista'}</p>
                            </div>
                          </td>
                          <td className="p-3">{emp.cpf || '-'}</td>
                          <td className="p-3">{emp.pis_pasep || '-'}</td>
                          <td className="p-3 text-center">{emp.total_hours}h</td>
                          <td className="p-3 text-center">
                            {emp.extra_hours > 0 && (
                              <Badge variant="secondary">{emp.extra_hours}h</Badge>
                            )}
                          </td>
                          <td className="p-3 text-center">
                            {emp.absences > 0 && (
                              <Badge variant="destructive">{emp.absences}</Badge>
                            )}
                            {emp.justified_absences > 0 && (
                              <Badge variant="outline" className="ml-1">{emp.justified_absences} just.</Badge>
                            )}
                          </td>
                          <td className="p-3 text-right">{(emp.base_value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</td>
                          <td className="p-3 text-right">{(emp.extra_value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</td>
                          <td className="p-3 text-right font-bold">{(emp.total_value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</td>
                          <td className="p-3">
                            <div className="text-xs">
                              {emp.bank_info.pix ? (
                                <span>PIX: {emp.bank_info.pix}</span>
                              ) : emp.bank_info.bank ? (
                                <span>{emp.bank_info.bank} Ag:{emp.bank_info.agency} Cc:{emp.bank_info.account}</span>
                              ) : (
                                <span className="text-muted-foreground">Não informado</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-muted/50 font-bold">
                      <tr>
                        <td colSpan={6} className="p-3 text-right">TOTAL:</td>
                        <td className="p-3 text-right">
                          {payrollReport.employees.reduce((sum, e) => sum + e.base_value, 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                        </td>
                        <td className="p-3 text-right">
                          {payrollReport.employees.reduce((sum, e) => sum + e.extra_value, 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                        </td>
                        <td className="p-3 text-right">
                          {payrollReport.employees.reduce((sum, e) => sum + e.total_value, 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                        </td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Clock In Dialog */}
      <Dialog open={clockDialogOpen} onOpenChange={(open) => { 
        setClockDialogOpen(open); 
        if (!open) {
          setLocation(null);
          setLocationError('');
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Registrar Ponto</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Funcionário</Label>
              <Select value={clockEmployee || undefined} onValueChange={setClockEmployee}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o funcionário" />
                </SelectTrigger>
                <SelectContent>
                  {employees.map(e => (
                    <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Tipo de Registro</Label>
              <Select value={clockType} onValueChange={setClockType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="entrada">
                    <div className="flex items-center gap-2">
                      <LogIn className="w-4 h-4 text-green-500" />
                      Entrada
                    </div>
                  </SelectItem>
                  <SelectItem value="saida_almoco">
                    <div className="flex items-center gap-2">
                      <Coffee className="w-4 h-4 text-amber-500" />
                      Saída Almoço
                    </div>
                  </SelectItem>
                  <SelectItem value="retorno_almoco">
                    <div className="flex items-center gap-2">
                      <Coffee className="w-4 h-4 text-blue-500" />
                      Retorno Almoço
                    </div>
                  </SelectItem>
                  <SelectItem value="saida">
                    <div className="flex items-center gap-2">
                      <LogOut className="w-4 h-4 text-red-500" />
                      Saída
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Localização GPS</Label>
              <div className="border rounded-lg p-4 bg-muted/30">
                {location ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-green-600">
                      <CheckCircle className="w-5 h-5" />
                      <span className="font-medium">Localização capturada!</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="p-2 bg-background rounded">
                        <p className="text-muted-foreground text-xs">Latitude</p>
                        <p className="font-mono">{location.latitude.toFixed(6)}</p>
                      </div>
                      <div className="p-2 bg-background rounded">
                        <p className="text-muted-foreground text-xs">Longitude</p>
                        <p className="font-mono">{location.longitude.toFixed(6)}</p>
                      </div>
                    </div>
                    {location.accuracy && (
                      <p className="text-xs text-muted-foreground">
                        Precisão: ~{Math.round(location.accuracy)} metros
                      </p>
                    )}
                    <div className="text-xs text-muted-foreground">
                      <a 
                        href={`https://www.google.com/maps?q=${location.latitude},${location.longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline flex items-center gap-1"
                      >
                        <MapPin className="w-3 h-3" />
                        Ver no Google Maps
                      </a>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    {gettingLocation ? (
                      <div className="flex flex-col items-center gap-2">
                        <Loader2 className="w-8 h-8 animate-spin text-primary" />
                        <p className="text-sm text-muted-foreground">Obtendo localização...</p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-2">
                        <MapPin className="w-12 h-12 text-muted-foreground/50" />
                        <p className="text-sm text-muted-foreground">Clique para capturar sua localização</p>
                      </div>
                    )}
                  </div>
                )}
                
                {locationError && (
                  <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-red-600 text-sm flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    {locationError}
                  </div>
                )}
              </div>
              
              <div className="flex gap-2">
                {!location && (
                  <Button 
                    type="button" 
                    onClick={getLocation} 
                    className="flex-1"
                    disabled={gettingLocation}
                  >
                    {gettingLocation ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Capturando...
                      </>
                    ) : (
                      <>
                        <MapPin className="w-4 h-4 mr-2" />
                        Capturar Localização
                      </>
                    )}
                  </Button>
                )}
                {location && (
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => { setLocation(null); getLocation(); }}
                    className="flex-1"
                  >
                    <MapPin className="w-4 h-4 mr-2" />
                    Atualizar Localização
                  </Button>
                )}
              </div>
            </div>

            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-2 text-blue-700 text-sm">
                <Clock className="w-4 h-4" />
                <span>
                  Horario atual: <strong>{spNow().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</strong>
                </span>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setClockDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleClockIn} disabled={!clockEmployee || !location}>
              Registrar Ponto
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Record Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Registro de Ponto</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Data e Hora</Label>
              <Input
                type="datetime-local"
                value={editTimestamp}
                onChange={(e) => setEditTimestamp(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Tipo de Registro</Label>
              <Select value={editType} onValueChange={setEditType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="entrada">Entrada</SelectItem>
                  <SelectItem value="saida_almoco">Saída Almoço</SelectItem>
                  <SelectItem value="retorno_almoco">Retorno Almoço</SelectItem>
                  <SelectItem value="saida">Saída</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Observações</Label>
              <Input
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                placeholder="Observações (opcional)"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleUpdateRecord}>
              <Save className="w-4 h-4 mr-2" />
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Timesheet Dialog (Folha Mensal) */}
      <Dialog open={editTimesheetDialogOpen} onOpenChange={setEditTimesheetDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Horarios - {editingTimesheet?.date ? spFormatDate(editingTimesheet.date + 'T12:00:00') : ''}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Entrada</Label>
                <Input
                  type="time"
                  value={editEntry}
                  onChange={(e) => setEditEntry(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Saída Almoço</Label>
                <Input
                  type="time"
                  value={editLunchOut}
                  onChange={(e) => setEditLunchOut(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Retorno Almoço</Label>
                <Input
                  type="time"
                  value={editLunchIn}
                  onChange={(e) => setEditLunchIn(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Saída</Label>
                <Input
                  type="time"
                  value={editExit}
                  onChange={(e) => setEditExit(e.target.value)}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTimesheetDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleUpdateTimesheet}>
              <Save className="w-4 h-4 mr-2" />
              Salvar Horários
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Justificativa */}
      <Dialog open={justificativaDialogOpen} onOpenChange={setJustificativaDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-orange-600" />
              Justificar Atraso ou Falta
            </DialogTitle>
            <DialogDescription>
              Envie uma justificativa para a coordenação
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Data *</Label>
              <Input
                type="date"
                value={justificativaDate}
                onChange={(e) => setJustificativaDate(e.target.value)}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Tipo *</Label>
              <Select value={justificativaTipo} onValueChange={setJustificativaTipo}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="atraso">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-amber-500" />
                      Atraso
                    </div>
                  </SelectItem>
                  <SelectItem value="falta">
                    <div className="flex items-center gap-2">
                      <XCircle className="w-4 h-4 text-red-500" />
                      Falta
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Justificativa *</Label>
              <Textarea
                placeholder="Descreva brevemente o motivo do atraso ou falta..."
                value={justificativaTexto}
                onChange={(e) => setJustificativaTexto(e.target.value)}
                rows={3}
                maxLength={500}
              />
              <p className="text-xs text-muted-foreground text-right">
                {justificativaTexto.length}/500 caracteres
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Atestado (opcional)</Label>
              <div className="border-2 border-dashed rounded-lg p-4 text-center">
                {atestadoFile ? (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FileCheck className="w-5 h-5 text-green-600" />
                      <span className="text-sm truncate max-w-[200px]">{atestadoFile.name}</span>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setAtestadoFile(null)}
                    >
                      <XCircle className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <label className="cursor-pointer">
                    <div className="flex flex-col items-center gap-2">
                      <Upload className="w-8 h-8 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">
                        Clique para anexar atestado médico
                      </span>
                      <span className="text-xs text-muted-foreground">
                        PDF, JPG ou PNG (máx. 5MB)
                      </span>
                    </div>
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          if (file.size > 5 * 1024 * 1024) {
                            toast.error('Arquivo muito grande. Máximo 5MB.');
                            return;
                          }
                          setAtestadoFile(file);
                        }
                      }}
                    />
                  </label>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setJustificativaDialogOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSubmitJustificativa}
              disabled={!justificativaTexto.trim() || uploadingAtestado}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {uploadingAtestado ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Enviar Justificativa
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Justificativas para Admin/Coordenação */}
      <Dialog open={showJustificativasModal} onOpenChange={setShowJustificativasModal}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Justificativas Pendentes
            </DialogTitle>
            <DialogDescription>
              Analise e aprove ou rejeite as justificativas enviadas pelos funcionários
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-3 py-4">
            {justificativas.filter(j => j.status === 'pendente').length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <CheckCircle className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Nenhuma justificativa pendente</p>
              </div>
            ) : (
              justificativas.filter(j => j.status === 'pendente').map(justificativa => (
                <Card key={justificativa.id} className="border-l-4 border-l-orange-400">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-semibold">{justificativa.employee_name}</span>
                          <Badge variant={justificativa.tipo === 'atraso' ? 'outline' : 'destructive'}>
                            {justificativa.tipo === 'atraso' ? 'Atraso' : 'Falta'}
                          </Badge>
                          <span className="text-sm text-muted-foreground">
                            {spFormatDate(justificativa.date + 'T12:00:00')}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">{justificativa.justificativa}</p>
                        {justificativa.atestado_url && (
                          <a 
                            href={justificativa.atestado_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
                          >
                            <Download className="w-4 h-4" />
                            Baixar Atestado
                          </a>
                        )}
                      </div>
                      <div className="flex gap-2 ml-4">
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-green-600 border-green-300 hover:bg-green-50"
                          onClick={() => handleAtualizarStatusJustificativa(justificativa.id, 'aprovado')}
                        >
                          <ThumbsUp className="w-4 h-4 mr-1" />
                          Aprovar
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 border-red-300 hover:bg-red-50"
                          onClick={() => handleAtualizarStatusJustificativa(justificativa.id, 'rejeitado')}
                        >
                          <ThumbsDown className="w-4 h-4 mr-1" />
                          Rejeitar
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowJustificativasModal(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
